import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { AuthProvider } from './contexts/AuthContext';
import { usePageTracking } from './hooks/useAnalytics';
import theme from './theme'; // Import the new theme

// Layouts
import MainLayout from './components/common/MainLayout';
import PublicLayout from './components/common/PublicLayout';

// Auth
import LoginForm from './components/auth/LoginForm';
import RegisterForm from './components/auth/RegisterForm';
import ProtectedRoute from './components/common/ProtectedRoute';

// Email Verification
import VerifyEmail from './pages/VerifyEmail';

// Password Reset
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';

// Public Pages
import HomePage from './pages/HomePage';
import AboutPage from './pages/AboutPage';
import ContactPage from './pages/ContactPage';

// Shared Pages
import Dashboard from './pages/Dashboard';
import Profile from './pages/Profile';
import TransactionsList from './pages/transactions/TransactionsList';

// Seller Pages
import InvoiceUpload from './pages/invoices/InvoiceUpload';
import InvoicesList from './pages/invoices/InvoicesList';
import InvoiceDetail from './pages/invoices/InvoiceDetail';

// Buyer Pages
import Marketplace from './pages/Marketplace';
import MarketplaceInvoiceDetail from './pages/marketplace/InvoiceDetail';
import BidsList from './pages/bids/BidsList';
import BidDetail from './pages/bids/BidDetail';

// Admin Pages
import CompaniesList from './pages/companies/CompaniesList';
import UsersList from './pages/users/UsersList';
import AdminInvoices from './pages/admin/AdminInvoices';

const Unauthorized = () => <div>Unauthorized Access</div>;

// Component to enable page tracking
const AppContent = () => {
  usePageTracking(); // Automatically track page views
  return null;
};

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AuthProvider>
        <Router>
          <AppContent />
          <Routes>
            {/* Public routes with PublicLayout */}
            <Route element={<PublicLayout />}>
              <Route path="/" element={<HomePage />} />
              <Route path="/about" element={<AboutPage />} />
              <Route path="/contact" element={<ContactPage />} />
              <Route path="/login" element={<LoginForm />} />
              <Route path="/register" element={<RegisterForm />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password/:token" element={<ResetPassword />} />
              <Route path="/verify-email/:token" element={<VerifyEmail />} />
              <Route path="/unauthorized" element={<Unauthorized />} />
            </Route>
            
            {/* Protected routes with MainLayout */}
            <Route element={<ProtectedRoute />}>
              <Route element={<MainLayout />}>
                <Route path="/dashboard" element={<Dashboard />} />
                <Route path="/profile" element={<Profile />} />
                
                {/* Seller routes */}
                <Route element={<ProtectedRoute requireSeller />}>
                  <Route path="/invoices" element={<InvoicesList />} />
                  <Route path="/invoices/upload" element={<InvoiceUpload />} />
                  <Route path="/invoices/:invoiceId" element={<InvoiceDetail />} />
                </Route>
                
                {/* Buyer routes */}
                <Route element={<ProtectedRoute requireBuyer />}>
                  <Route path="/marketplace" element={<Marketplace />} />
                  <Route path="/marketplace/:invoiceId" element={<MarketplaceInvoiceDetail />} />
                  <Route path="/bids" element={<BidsList />} />
                  <Route path="/bids/:bidId" element={<BidDetail />} />
                </Route>
                
                {/* Buyer or Seller routes */}
                <Route path="/transactions" element={<TransactionsList />} />
                
                {/* Admin routes */}
                <Route element={<ProtectedRoute requireAdmin />}>
                  <Route path="/companies" element={<CompaniesList />} />
                  <Route path="/users" element={<UsersList />} />
                  <Route path="/admin/invoices" element={<AdminInvoices />} />
                </Route>
              </Route>
            </Route>
            
            {/* Catch all route */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Router>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
